import { useMemo } from 'react';
import { usePartnerSettings } from './usePartnerSettings';

export interface RequirementViolation {
  field: string;
  fieldLabel: string;
  required: any;
  actual: any;
  severity: 'error' | 'warning';
  message: string;
}

export interface RequirementCheck {
  id: string;
  label: string;
  description: string;
  status: 'pass' | 'fail' | 'warning' | 'not_applicable' | 'pending_manual';
  required?: any;
  actual?: any;
  message?: string;
  canAutoVerify: boolean;
  requiresManualReview: boolean;
  manuallyVerified?: boolean;
  manualVerificationNote?: string;
}

export interface ManualVerificationState {
  [checkId: string]: {
    verified: boolean;
    note?: string;
  };
}

export interface RequirementsValidationResult {
  isValid: boolean;
  violations: RequirementViolation[];
  warnings: RequirementViolation[];
  errors: RequirementViolation[];
  checks: RequirementCheck[];
}

export function useDocumentRequirements(
  documentType: string,
  extractedData: any
): RequirementsValidationResult {
  const { settings } = usePartnerSettings();

  return useMemo(() => {
    const violations: RequirementViolation[] = [];
    const checks: RequirementCheck[] = [];

    if (!settings?.document_types || !extractedData) {
      return {
        isValid: true,
        violations: [],
        warnings: [],
        errors: [],
        checks: [],
      };
    }

    // Find document type configuration - match by ID or name for flexibility
    const docTypeConfig: any = settings.document_types.find(
      (dt: any) => dt.id === documentType || dt.name === documentType
    );

    // Debug logging
    console.log('🔍 Document Type Lookup:', {
      searchingFor: documentType,
      availableTypes: settings.document_types?.map((dt: any) => ({ id: dt.id, name: dt.name })),
      foundConfig: docTypeConfig ? { id: docTypeConfig.id, name: docTypeConfig.name } : 'NOT FOUND',
      hasRequirements: !!docTypeConfig?.requirements
    });

    if (!docTypeConfig?.requirements) {
      return {
        isValid: true,
        violations: [],
        warnings: [],
        errors: [],
        checks: [],
      };
    }

    const requirements: any = docTypeConfig.requirements;

    // Determine document category for field mapping
    const docTypeLower = documentType.toLowerCase();
    const isAutoInsurance = docTypeLower.includes('auto') || docTypeLower.includes('vehicle');
    
    // Check per occurrence coverage (or CSL for auto insurance)
    if (requirements.min_per_occurrence) {
      let actual: number | null = null;
      let fieldName = 'per_occurrence_limit';
      
      // Auto insurance uses Combined Single Limit instead of per occurrence
      if (isAutoInsurance) {
        actual = parseFloat(extractedData.combined_single_limit || extractedData.csl || '0');
        fieldName = 'combined_single_limit';
      } else {
        actual = parseFloat(extractedData.per_occurrence_limit || '0');
      }
      
      if (actual && actual > 0) {
        const required = requirements.min_per_occurrence;
        const passed = actual >= required;
        
        checks.push({
          id: fieldName,
          label: isAutoInsurance ? 'Combined Single Limit' : 'Per Occurrence Limit',
          description: `Minimum $${required.toLocaleString()} required`,
          status: passed ? 'pass' : 'fail',
          required: `$${required.toLocaleString()}`,
          actual: `$${actual.toLocaleString()}`,
          message: passed ? 'Meets requirement' : 'Below minimum requirement',
          canAutoVerify: true,
          requiresManualReview: false,
        });
        
        if (!passed) {
          violations.push({
            field: fieldName,
            fieldLabel: isAutoInsurance ? 'Combined Single Limit' : 'Per Occurrence Limit',
            required: `$${required.toLocaleString()}`,
            actual: `$${actual.toLocaleString()}`,
            severity: 'error',
            message: `Coverage limit is below minimum requirement`,
          });
        }
      } else {
        // Missing field - requires manual verification
        checks.push({
          id: fieldName,
          label: isAutoInsurance ? 'Combined Single Limit' : 'Per Occurrence Limit',
          description: `Minimum $${requirements.min_per_occurrence.toLocaleString()} required`,
          status: 'pending_manual',
          required: `$${requirements.min_per_occurrence.toLocaleString()}`,
          message: 'Could not extract from document - manual verification required',
          canAutoVerify: false,
          requiresManualReview: true,
        });
        
        violations.push({
          field: fieldName,
          fieldLabel: isAutoInsurance ? 'Combined Single Limit' : 'Per Occurrence Limit',
          required: `$${requirements.min_per_occurrence.toLocaleString()}`,
          actual: 'NOT FOUND',
          severity: 'warning',
          message: 'Coverage limit not extracted from document',
        });
      }
    }

    // Check aggregate coverage
    if (requirements.min_aggregate) {
      if (extractedData.aggregate_limit) {
        const actual = parseFloat(extractedData.aggregate_limit);
        const required = requirements.min_aggregate;
        const passed = actual >= required;
        
        checks.push({
          id: 'aggregate_limit',
          label: 'Aggregate Limit',
          description: `Minimum $${required.toLocaleString()} required`,
          status: passed ? 'pass' : 'fail',
          required: `$${required.toLocaleString()}`,
          actual: `$${actual.toLocaleString()}`,
          message: passed ? 'Meets requirement' : 'Below minimum requirement',
          canAutoVerify: true,
          requiresManualReview: false,
        });
        
        if (!passed) {
          violations.push({
            field: 'aggregate_limit',
            fieldLabel: 'Aggregate Limit',
            required: `$${required.toLocaleString()}`,
            actual: `$${actual.toLocaleString()}`,
            severity: 'error',
            message: `Aggregate coverage is below minimum requirement`,
          });
        }
      } else {
        checks.push({
          id: 'aggregate_limit',
          label: 'Aggregate Limit',
          description: `Minimum $${requirements.min_aggregate.toLocaleString()} required`,
          status: 'pending_manual',
          required: `$${requirements.min_aggregate.toLocaleString()}`,
          message: 'Could not extract from document - manual verification required',
          canAutoVerify: false,
          requiresManualReview: true,
        });
        
        violations.push({
          field: 'aggregate_limit',
          fieldLabel: 'Aggregate Limit',
          required: `$${requirements.min_aggregate.toLocaleString()}`,
          actual: 'NOT FOUND',
          severity: 'warning',
          message: 'Aggregate limit not extracted from document',
        });
      }
    }

    // Check additional insured requirement
    if (requirements.additional_insured_required) {
      const fieldValue = extractedData.additional_insured;
      const hasAdditionalInsured = fieldValue === true || fieldValue === 'yes';
      const fieldExists = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      
      if (fieldExists) {
        checks.push({
          id: 'additional_insured',
          label: 'Additional Insured',
          description: 'Must be listed on the policy',
          status: hasAdditionalInsured ? 'pass' : 'fail',
          required: 'Required',
          actual: hasAdditionalInsured ? 'Listed' : 'Not listed',
          message: hasAdditionalInsured ? 'Requirement met' : 'Additional insured not found',
          canAutoVerify: true,
          requiresManualReview: false,
        });
        
        if (!hasAdditionalInsured) {
          violations.push({
            field: 'additional_insured',
            fieldLabel: 'Additional Insured',
            required: 'Required',
            actual: 'Not listed',
            severity: 'error',
            message: `Additional insured status is required but not found on policy`,
          });
        }
      } else {
        // Field not extracted - requires manual verification
        checks.push({
          id: 'additional_insured',
          label: 'Additional Insured',
          description: 'Must be listed on the policy',
          status: 'pending_manual',
          required: 'Required',
          message: 'Could not extract from document - manual verification required',
          canAutoVerify: false,
          requiresManualReview: true,
        });
        
        violations.push({
          field: 'additional_insured',
          fieldLabel: 'Additional Insured',
          required: 'Required',
          actual: 'NOT FOUND',
          severity: 'warning',
          message: 'Additional insured status not extracted from document',
        });
      }
    }

    // Check waiver of subrogation requirement
    if (requirements.waiver_of_subrogation_required) {
      const fieldValue = extractedData.waiver_of_subrogation;
      const hasWaiver = fieldValue === true || fieldValue === 'yes';
      const fieldExists = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      
      if (fieldExists) {
        checks.push({
          id: 'waiver_of_subrogation',
          label: 'Waiver of Subrogation',
          description: 'Must be included on the policy',
          status: hasWaiver ? 'pass' : 'fail',
          required: 'Required',
          actual: hasWaiver ? 'Included' : 'Not included',
          message: hasWaiver ? 'Requirement met' : 'Waiver not found',
          canAutoVerify: true,
          requiresManualReview: false,
        });
        
        if (!hasWaiver) {
          violations.push({
            field: 'waiver_of_subrogation',
            fieldLabel: 'Waiver of Subrogation',
            required: 'Required',
            actual: 'Not included',
            severity: 'error',
            message: `Waiver of subrogation is required but not found on policy`,
          });
        }
      } else {
        // Field not extracted - requires manual verification
        checks.push({
          id: 'waiver_of_subrogation',
          label: 'Waiver of Subrogation',
          description: 'Must be included on the policy',
          status: 'pending_manual',
          required: 'Required',
          message: 'Could not extract from document - manual verification required',
          canAutoVerify: false,
          requiresManualReview: true,
        });
        
        violations.push({
          field: 'waiver_of_subrogation',
          fieldLabel: 'Waiver of Subrogation',
          required: 'Required',
          actual: 'NOT FOUND',
          severity: 'warning',
          message: 'Waiver of subrogation not extracted from document',
        });
      }
    }

    // Check primary & non-contributory requirement
    if (requirements.primary_noncontributory_required) {
      const fieldValue = extractedData.primary_noncontributory;
      const hasPrimaryNonContrib = fieldValue === true || fieldValue === 'yes';
      const fieldExists = fieldValue !== undefined && fieldValue !== null && fieldValue !== '';
      
      if (fieldExists) {
        checks.push({
          id: 'primary_noncontributory',
          label: 'Primary & Non-Contributory',
          description: 'Must be designated as primary coverage',
          status: hasPrimaryNonContrib ? 'pass' : 'fail',
          required: 'Required',
          actual: hasPrimaryNonContrib ? 'Designated' : 'Not designated',
          message: hasPrimaryNonContrib ? 'Requirement met' : 'Primary & non-contributory not found',
          canAutoVerify: true,
          requiresManualReview: false,
        });
        
        if (!hasPrimaryNonContrib) {
          violations.push({
            field: 'primary_noncontributory',
            fieldLabel: 'Primary & Non-Contributory',
            required: 'Required',
            actual: 'Not designated',
            severity: 'error',
            message: `Primary & non-contributory designation is required but not found`,
          });
        }
      } else {
        // Field not extracted - requires manual verification
        checks.push({
          id: 'primary_noncontributory',
          label: 'Primary & Non-Contributory',
          description: 'Must be designated as primary coverage',
          status: 'pending_manual',
          required: 'Required',
          message: 'Could not extract from document - manual verification required',
          canAutoVerify: false,
          requiresManualReview: true,
        });
        
        violations.push({
          field: 'primary_noncontributory',
          fieldLabel: 'Primary & Non-Contributory',
          required: 'Required',
          actual: 'NOT FOUND',
          severity: 'warning',
          message: 'Primary & non-contributory designation not extracted from document',
        });
      }
    }

    // Check AM Best rating
    if (requirements.min_am_best_rating && extractedData.am_best_rating) {
      const actual = extractedData.am_best_rating;
      const required = requirements.min_am_best_rating;
      // Simple comparison: A+ > A > A- > B+ etc.
      const passed = actual.localeCompare(required) >= 0;
      
      checks.push({
        id: 'am_best_rating',
        label: 'AM Best Rating',
        description: `Minimum ${required} rating required`,
        status: passed ? 'pass' : 'warning',
        required,
        actual,
        message: passed ? 'Meets requirement' : 'Rating below recommended minimum',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'am_best_rating',
          fieldLabel: 'AM Best Rating',
          required,
          actual,
          severity: 'warning',
          message: `Carrier rating is below recommended minimum`,
        });
      }
    }

    // Check admitted carrier requirement
    if (requirements.admitted_carrier_required) {
      const isAdmitted = extractedData.admitted_carrier === true || 
                         extractedData.admitted_carrier === 'yes';
      
      checks.push({
        id: 'admitted_carrier',
        label: 'Admitted Carrier',
        description: 'Must be an admitted carrier',
        status: isAdmitted ? 'pass' : 'warning',
        required: 'Required',
        actual: isAdmitted ? 'Admitted' : 'Non-admitted',
        message: isAdmitted ? 'Requirement met' : 'Non-admitted carrier',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!isAdmitted) {
        violations.push({
          field: 'admitted_carrier',
          fieldLabel: 'Admitted Carrier',
          required: 'Admitted',
          actual: 'Non-admitted',
          severity: 'warning',
          message: `Non-admitted carrier may require additional review`,
        });
      }
    }

    // Check expiry date
    if (requirements.min_days_until_expiry && extractedData.expiry_date) {
      const expiryDate = new Date(extractedData.expiry_date);
      const today = new Date();
      const daysUntilExpiry = Math.floor((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      let status: 'pass' | 'fail' | 'warning' = 'pass';
      let message = `Valid until ${expiryDate.toLocaleDateString()}`;
      
      if (daysUntilExpiry < 0) {
        status = 'fail';
        message = 'Document has expired';
        violations.push({
          field: 'expiry_date',
          fieldLabel: 'Expiry Date',
          required: 'Not expired',
          actual: 'Expired',
          severity: 'error',
          message: `Document has already expired`,
        });
      } else if (daysUntilExpiry < requirements.min_days_until_expiry) {
        status = 'warning';
        message = `Expires soon (${daysUntilExpiry} days remaining)`;
        violations.push({
          field: 'expiry_date',
          fieldLabel: 'Expiry Date',
          required: `At least ${requirements.min_days_until_expiry} days`,
          actual: `${daysUntilExpiry} days remaining`,
          severity: 'warning',
          message: `Document will expire soon`,
        });
      }
      
      checks.push({
        id: 'expiry_date',
        label: 'Expiry Date',
        description: `Must have at least ${requirements.min_days_until_expiry} days remaining`,
        status,
        required: `At least ${requirements.min_days_until_expiry} days`,
        actual: daysUntilExpiry < 0 ? 'Expired' : `${daysUntilExpiry} days remaining`,
        message,
        canAutoVerify: true,
        requiresManualReview: false,
      });
    }

    // Check license active status
    if (requirements.active_status_required && extractedData.license_status) {
      const isActive = extractedData.license_status.toLowerCase() === 'active';
      
      checks.push({
        id: 'license_status',
        label: 'License Status',
        description: 'Must be in active status',
        status: isActive ? 'pass' : 'fail',
        required: 'Active',
        actual: extractedData.license_status,
        message: isActive ? 'License is active' : 'License is not active',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!isActive) {
        violations.push({
          field: 'license_status',
          fieldLabel: 'License Status',
          required: 'Active',
          actual: extractedData.license_status,
          severity: 'error',
          message: `License must be in active status`,
        });
      }
    }

    // Check license monetary limit
    if (requirements.min_monetary_limit && extractedData.monetary_limit) {
      const actual = parseFloat(extractedData.monetary_limit);
      const required = requirements.min_monetary_limit;
      const passed = actual >= required;
      
      checks.push({
        id: 'monetary_limit',
        label: 'License Monetary Limit',
        description: `Minimum $${required.toLocaleString()} required`,
        status: passed ? 'pass' : 'warning',
        required: `$${required.toLocaleString()}`,
        actual: `$${actual.toLocaleString()}`,
        message: passed ? 'Meets requirement' : 'Below recommended limit',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'monetary_limit',
          fieldLabel: 'License Monetary Limit',
          required: `$${required.toLocaleString()}`,
          actual: `$${actual.toLocaleString()}`,
          severity: 'warning',
          message: `License monetary limit is below recommended amount`,
        });
      }
    }

    // Check bonding single project limit
    if (requirements.min_single_project_limit && extractedData.single_project_limit) {
      const actual = parseFloat(extractedData.single_project_limit);
      const required = requirements.min_single_project_limit;
      const passed = actual >= required;
      
      checks.push({
        id: 'single_project_limit',
        label: 'Single Project Bonding Limit',
        description: `Minimum $${required.toLocaleString()} required`,
        status: passed ? 'pass' : 'fail',
        required: `$${required.toLocaleString()}`,
        actual: `$${actual.toLocaleString()}`,
        message: passed ? 'Meets requirement' : 'Below minimum requirement',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'single_project_limit',
          fieldLabel: 'Single Project Limit',
          required: `$${required.toLocaleString()}`,
          actual: `$${actual.toLocaleString()}`,
          severity: 'error',
          message: `Single project bonding capacity is below minimum`,
        });
      }
    }

    // Check bonding aggregate capacity
    if (requirements.min_aggregate_capacity && extractedData.aggregate_capacity) {
      const actual = parseFloat(extractedData.aggregate_capacity);
      const required = requirements.min_aggregate_capacity;
      const passed = actual >= required;
      
      checks.push({
        id: 'aggregate_capacity',
        label: 'Total Bonding Capacity',
        description: `Minimum $${required.toLocaleString()} required`,
        status: passed ? 'pass' : 'fail',
        required: `$${required.toLocaleString()}`,
        actual: `$${actual.toLocaleString()}`,
        message: passed ? 'Meets requirement' : 'Below minimum requirement',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'aggregate_capacity',
          fieldLabel: 'Aggregate Capacity',
          required: `$${required.toLocaleString()}`,
          actual: `$${actual.toLocaleString()}`,
          severity: 'error',
          message: `Total bonding capacity is below minimum`,
        });
      }
    }

    // Check EMR (Experience Modification Rate)
    if (requirements.max_emr && extractedData.emr) {
      const actual = parseFloat(extractedData.emr);
      const required = requirements.max_emr;
      const passed = actual <= required;
      
      checks.push({
        id: 'emr',
        label: 'EMR (Experience Modification Rate)',
        description: `Maximum ${required} required`,
        status: passed ? 'pass' : 'warning',
        required: `${required} or below`,
        actual: actual.toString(),
        message: passed ? 'Within acceptable range' : 'Above recommended maximum',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'emr',
          fieldLabel: 'EMR',
          required: `${required} or below`,
          actual: actual.toString(),
          severity: 'warning',
          message: `EMR is above recommended maximum`,
        });
      }
    }

    // Check TIN requirement (W-9)
    if (requirements.tin_required) {
      const hasTIN = extractedData.tin || extractedData.ein;
      
      checks.push({
        id: 'tin',
        label: 'Tax Identification Number',
        description: 'TIN/EIN must be provided',
        status: hasTIN ? 'pass' : 'fail',
        required: 'Required',
        actual: hasTIN ? 'Provided' : 'Not provided',
        message: hasTIN ? 'TIN found' : 'TIN not found',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!hasTIN) {
        violations.push({
          field: 'tin',
          fieldLabel: 'TIN/EIN',
          required: 'Required',
          actual: 'Not found',
          severity: 'error',
          message: `Taxpayer identification number is required`,
        });
      }
    }

    // Check document age
    if (requirements.max_age_days && extractedData.issue_date) {
      const issueDate = new Date(extractedData.issue_date);
      const today = new Date();
      const ageInDays = Math.floor((today.getTime() - issueDate.getTime()) / (1000 * 60 * 60 * 24));
      const passed = ageInDays <= requirements.max_age_days;
      
      checks.push({
        id: 'document_age',
        label: 'Document Age',
        description: `Must be dated within ${requirements.max_age_days} days`,
        status: passed ? 'pass' : 'warning',
        required: `Within ${requirements.max_age_days} days`,
        actual: `${ageInDays} days old`,
        message: passed ? 'Within acceptable timeframe' : 'Document may be outdated',
        canAutoVerify: true,
        requiresManualReview: false,
      });
      
      if (!passed) {
        violations.push({
          field: 'issue_date',
          fieldLabel: 'Document Age',
          required: `Within ${requirements.max_age_days} days`,
          actual: `${ageInDays} days old`,
          severity: 'warning',
          message: `Document may be outdated`,
        });
      }
    }

    const errors = violations.filter(v => v.severity === 'error');
    const warnings = violations.filter(v => v.severity === 'warning');

    return {
      isValid: errors.length === 0,
      violations,
      warnings,
      errors,
      checks,
    };
  }, [documentType, extractedData, settings]);
}

/**
 * Apply manual verification state to a validation result
 * Returns a new validation result with manual verifications applied
 */
export function applyManualVerifications(
  validation: RequirementsValidationResult,
  manualState: ManualVerificationState
): RequirementsValidationResult {
  const updatedChecks = validation.checks.map(check => {
    if (check.requiresManualReview && manualState[check.id]) {
      return {
        ...check,
        manuallyVerified: manualState[check.id].verified,
        manualVerificationNote: manualState[check.id].note,
        status: (manualState[check.id].verified ? 'pass' : 'pending_manual') as RequirementCheck['status'],
      };
    }
    return check;
  });
  
  // Recalculate isValid with manual verifications
  const allChecksPassed = updatedChecks.every(check => 
    check.status === 'pass' || check.status === 'warning'
  );
  
  return { 
    ...validation, 
    checks: updatedChecks, 
    isValid: allChecksPassed 
  };
}
